import React from 'react';
import { X, Crown, ShieldCheck, Download, Monitor, Smartphone, CheckCircle2, ExternalLink } from 'lucide-react';
import { usePWAInstall } from '../utils/pwaInstall';

interface AdminInstallGuideModalProps {
  isOpen: boolean;
  onClose: () => void;
  role?: 'super_admin' | 'admin';
}

export const AdminInstallGuideModal: React.FC<AdminInstallGuideModalProps> = ({
  isOpen,
  onClose,
  role = 'super_admin'
}) => {
  const { isAdminPromptReady, platform, triggerInstall } = usePWAInstall();
  const { isIOS, isDesktop, isWindows, isMac } = platform;
  const isSuper = role === 'super_admin';

  const isAtDirectAdminUrl =
    typeof window !== 'undefined' &&
    ((window as any).__INITIAL_HTTP_PATHNAME__ || window.location.pathname.toLowerCase()).includes(
      isSuper ? '/super-admin' : '/admin'
    );

  React.useEffect(() => {
    if (isOpen) {
      localStorage.setItem('blishka_installed_portal', isSuper ? 'super-admin' : 'admin');
    }
  }, [isOpen, isSuper]);

  if (!isOpen) return null;

  const handleLaunchDirectAdminUrl = () => {
    try {
      sessionStorage.setItem('blishka_admin_auth', 'true');
      sessionStorage.setItem('blishka_admin_role', isSuper ? 'super_admin' : 'admin');
      const portalKey = isSuper ? 'super-admin' : 'admin';
      localStorage.setItem('blishka_installed_portal', portalKey);
      document.cookie = `blishka_installed_portal=${portalKey}; path=/; max-age=31536000; SameSite=Lax`;
    } catch {}
    const targetPath = isSuper ? '/super-admin' : '/admin';
    window.location.href = `${targetPath}?source=pwa&app=admin&install=true`;
  };

  const handleNativeInstall = async () => {
    if (isAdminPromptReady) {
      const outcome = await triggerInstall('admin');
      if (outcome === 'accepted') {
        onClose();
      } else if (outcome === 'portal_mismatch') {
        handleLaunchDirectAdminUrl();
      }
    } else {
      handleLaunchDirectAdminUrl();
    }
  };

  return (
    <div className="pwa-guide-backdrop" onClick={onClose} style={{ zIndex: 11000 }}>
      <div className="pwa-guide-card" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '480px' }}>
        {/* Header */}
        <div className="pwa-guide-header">
          <div
            className="pwa-guide-icon-badge"
            style={{
              background: isSuper
                ? 'linear-gradient(135deg, #b8860b 0%, #7d5a00 100%)'
                : 'linear-gradient(135deg, var(--blishka-berry) 0%, #4a0a1f 100%)',
              color: '#ffffff'
            }}
          >
            {isSuper ? <Crown size={22} /> : <ShieldCheck size={22} />}
          </div>
          <div className="pwa-guide-title-box">
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.45rem' }}>
              <h4 className="pwa-guide-title" style={{ margin: 0, fontSize: '1.05rem', color: isSuper ? '#7d5a00' : 'var(--blishka-berry)' }}>
                {isSuper ? 'Blishka Super Admin' : 'Blishka Admin'}
              </h4>
              <span
                style={{
                  fontSize: '0.65rem',
                  fontWeight: 800,
                  background: isSuper ? 'rgba(184, 134, 11, 0.14)' : 'rgba(125, 26, 56, 0.1)',
                  color: isSuper ? '#9a7000' : 'var(--blishka-berry)',
                  padding: '0.12rem 0.45rem',
                  borderRadius: '999px',
                  border: isSuper ? '1px solid rgba(184, 134, 11, 0.3)' : 'none'
                }}
              >
                {isSuper ? 'EXECUTIVE' : 'OPERATIONS'}
              </span>
            </div>
            <p className="pwa-guide-sub" style={{ margin: '0.15rem 0 0', fontSize: '0.78rem' }}>
              Standalone Desktop &amp; Mobile Dashboard App
            </p>
          </div>
          <button
            type="button"
            className="pwa-guide-close"
            onClick={onClose}
            aria-label="Close guide"
          >
            <X size={18} />
          </button>
        </div>

        {/* Body */}
        <div className="pwa-guide-body" style={{ padding: '1.25rem' }}>
          {/* 1-Tap Direct Install for Chrome / Edge when Admin Prompt is Ready */}
          {isAdminPromptReady && !isIOS && (
            <div
              style={{
                background: isSuper ? 'linear-gradient(135deg, #fffdf7 0%, #fef8e7 100%)' : 'linear-gradient(135deg, #fdf8f9 0%, #faeef1 100%)',
                border: isSuper ? '1.5px solid rgba(184, 134, 11, 0.4)' : '1.5px solid rgba(125, 26, 56, 0.25)',
                borderRadius: '14px',
                padding: '1rem',
                marginBottom: '1.25rem',
                textAlign: 'center'
              }}
            >
              <div style={{ fontSize: '0.88rem', fontWeight: 700, color: isSuper ? '#7d5a00' : 'var(--blishka-berry)', marginBottom: '0.35rem' }}>
                ⚡ 1-Click Install Available
              </div>
              <p style={{ fontSize: '0.78rem', color: '#665357', margin: '0 0 0.85rem', lineHeight: 1.4 }}>
                {isDesktop
                  ? `Install directly onto your ${isWindows ? 'Windows PC' : isMac ? 'Mac' : 'computer'} as a native standalone desktop app.`
                  : 'Install onto your phone as a standalone administrative app with zero browser bars.'}
              </p>
              <button
                type="button"
                onClick={handleNativeInstall}
                className="btn"
                style={{
                  width: '100%',
                  padding: '0.65rem',
                  fontSize: '0.9rem',
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '0.5rem',
                  borderRadius: '10px',
                  background: isSuper ? 'linear-gradient(135deg, #b8860b 0%, #d4af37 100%)' : 'var(--blishka-berry)',
                  color: '#ffffff',
                  border: 'none',
                  fontWeight: 700
                }}
              >
                <Download size={16} />
                <span>Install Blishka Admin App</span>
              </button>
            </div>
          )}

          {/* If prompt is NOT ready or user arrived from client-side SPA navigation, provide 1-click transition to direct URL */}
          {!isAdminPromptReady && !isIOS && (
            <div
              style={{
                background: isSuper ? 'linear-gradient(135deg, #fffdf7 0%, #fef8e7 100%)' : 'linear-gradient(135deg, #fdf8f9 0%, #faeef1 100%)',
                border: isSuper ? '1.5px solid rgba(184, 134, 11, 0.4)' : '1.5px solid rgba(125, 26, 56, 0.25)',
                borderRadius: '14px',
                padding: '1rem',
                marginBottom: '1.25rem',
                textAlign: 'center'
              }}
            >
              <div style={{ fontSize: '0.88rem', fontWeight: 700, color: isSuper ? '#7d5a00' : 'var(--blishka-berry)', marginBottom: '0.35rem' }}>
                ⚡ Install Dedicated {isSuper ? 'Super Admin' : 'Admin'} App
              </div>
              <p style={{ fontSize: '0.78rem', color: '#665357', margin: '0 0 0.85rem', lineHeight: 1.4 }}>
                To install the dedicated standalone Admin app with its own independent icon &amp; desktop window, open the direct Admin URL below:
              </p>
              <button
                type="button"
                onClick={handleLaunchDirectAdminUrl}
                className="btn"
                style={{
                  width: '100%',
                  padding: '0.65rem',
                  fontSize: '0.9rem',
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '0.5rem',
                  borderRadius: '10px',
                  background: isSuper ? 'linear-gradient(135deg, #b8860b 0%, #d4af37 100%)' : 'var(--blishka-berry)',
                  color: '#ffffff',
                  border: 'none',
                  fontWeight: 700
                }}
              >
                <ExternalLink size={16} />
                <span>
                  {isAtDirectAdminUrl
                    ? `Reload Direct ${isSuper ? 'Super Admin' : 'Admin'} App`
                    : `Launch Direct ${isSuper ? 'Super Admin' : 'Admin'} URL to Install`}
                </span>
              </button>
            </div>
          )}

          {/* Platform Specific Instructions */}
          {isDesktop ? (
            <div className="pwa-android-help-content">
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.4rem',
                  color: isSuper ? '#7d5a00' : 'var(--blishka-berry)',
                  fontWeight: 700,
                  fontSize: '0.86rem',
                  marginBottom: '0.5rem'
                }}
              >
                <Monitor size={16} />
                <span>Desktop (Google Chrome / Microsoft Edge) Setup:</span>
              </div>
              <ol className="pwa-steps-list" style={{ marginTop: '0.5rem' }}>
                <li>
                  Click the <strong>&quot;Install&quot;</strong> icon (<strong>⊕</strong> or computer monitor icon) in the right side of the browser URL address bar.
                </li>
                <li>
                  Or click the browser menu (<strong>⋮</strong> or <strong>...</strong>) &rarr; <strong>Save and Share / Apps</strong> &rarr; select <strong>&quot;Install Blishka Admin&quot;</strong>.
                </li>
                <li>
                  Click <strong>&quot;Install&quot;</strong> in the confirmation dialog.
                </li>
              </ol>
              <div
                style={{
                  background: 'rgba(46, 125, 50, 0.08)',
                  border: '1px solid rgba(46, 125, 50, 0.2)',
                  borderRadius: '10px',
                  padding: '0.65rem 0.85rem',
                  marginTop: '0.85rem',
                  fontSize: '0.78rem',
                  color: '#2e7d32',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.45rem'
                }}
              >
                <CheckCircle2 size={16} style={{ flexShrink: 0 }} />
                <span>Opens in an independent desktop window and pins to your Taskbar / Dock!</span>
              </div>
            </div>
          ) : isIOS ? (
            <div className="pwa-android-help-content">
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.4rem',
                  color: isSuper ? '#7d5a00' : 'var(--blishka-berry)',
                  fontWeight: 700,
                  fontSize: '0.86rem',
                  marginBottom: '0.65rem'
                }}
              >
                <Smartphone size={16} />
                <span>iPhone / iPad (Safari) Setup:</span>
              </div>

              {/* Status or Direct URL Switcher for iOS */}
              {isAtDirectAdminUrl ? (
                <div
                  style={{
                    background: 'rgba(46, 125, 50, 0.08)',
                    border: '1px solid rgba(46, 125, 50, 0.25)',
                    borderRadius: '10px',
                    padding: '0.5rem 0.75rem',
                    marginBottom: '0.85rem',
                    fontSize: '0.78rem',
                    color: '#2e7d32',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.45rem'
                  }}
                >
                  <CheckCircle2 size={16} style={{ flexShrink: 0 }} />
                  <span>Safari is open at <strong>blishka.in/{isSuper ? 'super-admin' : 'admin'}</strong></span>
                </div>
              ) : (
                <div style={{ marginBottom: '0.85rem' }}>
                  <p style={{ fontSize: '0.76rem', color: '#665357', margin: '0 0 0.5rem', lineHeight: 1.35 }}>
                    ⚠️ Open the direct URL in Safari first so iPhone saves the Super Admin dashboard (not the customer home):
                  </p>
                  <button
                    type="button"
                    onClick={handleLaunchDirectAdminUrl}
                    className="btn"
                    style={{
                      width: '100%',
                      padding: '0.6rem',
                      fontSize: '0.85rem',
                      fontWeight: 700,
                      background: isSuper ? 'linear-gradient(135deg, #b8860b 0%, #d4af37 100%)' : 'var(--blishka-berry)',
                      color: '#ffffff',
                      borderRadius: '8px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '0.4rem'
                    }}
                  >
                    <ExternalLink size={15} />
                    <span>Open Direct {isSuper ? 'Super Admin' : 'Admin'} Safari URL</span>
                  </button>
                </div>
              )}

              <ol className="pwa-steps-list" style={{ marginTop: '0.5rem' }}>
                <li>
                  Tap Safari&apos;s <strong>Share button</strong> (the square with arrow pointing up <strong>⎋</strong>) at the bottom toolbar of your iPhone.
                </li>
                <li>
                  Scroll down the share sheet and tap <strong>&quot;Add to Home Screen&quot;</strong> (<strong>➕</strong>).
                </li>
                <li>
                  Verify the title displays <strong>&quot;{isSuper ? 'Blishka Super Admin' : 'Blishka Admin'}&quot;</strong> and tap <strong>&quot;Add&quot;</strong> in the top-right corner.
                </li>
              </ol>

              <div
                style={{
                  background: 'rgba(46, 125, 50, 0.08)',
                  border: '1px solid rgba(46, 125, 50, 0.2)',
                  borderRadius: '10px',
                  padding: '0.65rem 0.85rem',
                  marginTop: '0.85rem',
                  fontSize: '0.78rem',
                  color: '#2e7d32',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.45rem'
                }}
              >
                <CheckCircle2 size={16} style={{ flexShrink: 0 }} />
                <span>Installs with &quot;{isSuper ? 'Blishka Super Admin' : 'Blishka Admin'}&quot; icon and launches directly into Super Admin!</span>
              </div>
            </div>
          ) : (
            <div className="pwa-android-help-content">
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.4rem',
                  color: isSuper ? '#7d5a00' : 'var(--blishka-berry)',
                  fontWeight: 700,
                  fontSize: '0.86rem',
                  marginBottom: '0.5rem'
                }}
              >
                <Smartphone size={16} />
                <span>Android Chrome / Samsung Setup:</span>
              </div>
              <ol className="pwa-steps-list" style={{ marginTop: '0.5rem' }}>
                <li>Tap <strong>&quot;Install Blishka Admin App&quot;</strong> above, or tap Chrome&apos;s <strong>Menu (⋮)</strong>.</li>
                <li>Select <strong>&quot;Install app&quot;</strong> / <strong>&quot;Add to Home screen&quot;</strong>.</li>
                <li>Your phone creates an app icon named <strong>&quot;Blishka Admin&quot;</strong>.</li>
              </ol>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="pwa-guide-footer" style={{ padding: '0.85rem 1.25rem' }}>
          <button
            type="button"
            className="pwa-guide-btn-primary"
            onClick={onClose}
            style={{
              width: '100%',
              background: isSuper ? 'linear-gradient(135deg, #b8860b 0%, #7d5a00 100%)' : 'var(--blishka-berry)'
            }}
          >
            Done / Got It
          </button>
        </div>
      </div>
    </div>
  );
};
